import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { InputRow, SelectRow, SwitchRow } from "./Rows";
import { FormCard, FormGrid, FormSection } from "./FormCard";
import { buildHeavyVehicleSummary } from "./site/locationCardSummaries";
import { withTranslatedLabels } from "../i18n/options";

const selectableValues = {
  suitability: [
    { value: "LCV_TRACTOR", label: "LCV Tractor" },
    { value: "BOX_TRUCK", label: "Box Truck" },
    { value: "TRUCK_TRAILER", label: "Truck Trailer" },
  ],
  cable_side: [
    { value: "LEFT", label: "Left" },
    { value: "RIGHT", label: "Right" },
    { value: "FRONT", label: "Front" },
  ],
  amenities: [
    { value: "TRUCK_PARKING", label: "Truck Parking" },
    { value: "TRUCK_WASH", label: "Truck Wash" },
    { value: "TRUCK_REPAIR", label: "Truck Repair" },
    { value: "TRUCK_DEALERSHIP", label: "Truck Dealership" },
    { value: "SECURE_TRUCK_PARKING", label: "Secure Truck Parking" },
    { value: "FOOD_DELIVERY", label: "Food Delivery" },
    { value: "TOILET", label: "Toilet" },
    { value: "WASHING_MACHINE", label: "Washing Machine" },
    { value: "SHOWER", label: "Shower" },
    { value: "RESTAURANT", label: "Restaurant" },
    { value: "VENDING_MACHINE", label: "Vending Machine" },
    { value: "DRIVER_LOUNGE", label: "Driver Lounge" },
    { value: "OUTDOOR_GYM", label: "Outdoor Gym" },
    { value: "POWER_SUPPLY", label: "Power Supply" },
    { value: "ATM", label: "ATM" },
    { value: "ACCESS_CONTROLLED_AREA", label: "Access Controlled Area" },
    {
      value: "LICENSE_PLATE_RECOGNITION",
      label: "License Plate Recognition",
    },
    { value: "PERIMETER_FENCE", label: "Perimeter Fence" },
    { value: "PEDESTRIAN_TURNSTILES", label: "Pedestrian Turnstiles" },
    { value: "CAMERA_SURVEILLANCE", label: "Camera Surveillance" },
    { value: "GUARD", label: "Guard" },
    { value: "FLOOD_LIGHTING", label: "Flood Lighting" },
    { value: "ENTRY_GATE", label: "Entry Gate" },
    { value: "EXIT_GATE", label: "Exit Gate" },
    { value: "UNKNOWN", label: "Unknown" },
  ],
};

const parseNumber = (value) => {
  if (value === undefined || value === null || value === "") {
    return "";
  }

  let nextValue = String(value).replace(",", ".");
  nextValue = nextValue.replace(/[^0-9.-]/g, "");

  const parts = nextValue.split(".");
  if (parts.length > 2) {
    nextValue = parts[0] + "." + parts.slice(1).join("");
  }

  if (nextValue.includes("-")) {
    nextValue = "-" + nextValue.replace(/-/g, "");
  }

  return nextValue;
};

function HeavyVehicleEditor({ data, onChange = () => {} }) {
  const { t } = useTranslation();
  const [vehicle, setVehicle] = useState(data);
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    if (hasMounted) {
      onChange(vehicle);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, [vehicle]);

  useEffect(() => {
    setVehicle(data);
    if (!hasMounted) {
      setHasMounted(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, [data]);

  const handleChange = (field, value) => {
    setVehicle((prev) => ({ ...prev, [field]: value }));
  };

  const suitabilityOptions = withTranslatedLabels(
    selectableValues.suitability,
    t,
    "site.location.suitabilityValue"
  );
  const cableSideOptions = withTranslatedLabels(
    selectableValues.cable_side,
    t,
    "site.location.cableSideValue"
  );
  const amenityOptions = withTranslatedLabels(
    selectableValues.amenities,
    t,
    "site.location.amenity"
  );

  return (
    <div className="w-full">
      <FormCard
        title={t("site.location.heavyTitle")}
        description={t("site.location.heavyDescription")}
        summary={buildHeavyVehicleSummary(vehicle, suitabilityOptions, t)}
        collapsible
        defaultOpen={false}
      >
        <FormGrid>
          <SelectRow
            label={t("site.location.suitability")}
            value={vehicle?.suitability}
            options={suitabilityOptions}
            onChange={(value) => handleChange("suitability", value)}
            isMulti={false}
          />

          <SelectRow
            label={t("site.location.cableSide")}
            value={vehicle?.cable_side || []}
            options={cableSideOptions}
            onChange={(value) => handleChange("cable_side", value)}
            isMulti
          />

          <FormSection title={t("site.location.dimensions")} className="col-span-1 md:col-span-2 mt-10">
            <FormGrid>
              <InputRow
                label={t("site.location.heightM")}
                value={vehicle?.height}
                isNumber
                onChange={(value) => handleChange("height", parseNumber(value))}
              />

              <InputRow
                label={t("site.location.widthM")}
                value={vehicle?.width}
                isNumber
                onChange={(value) => handleChange("width", parseNumber(value))}
              />

              <InputRow
                label={t("site.location.lengthM")}
                value={vehicle?.length}
                isNumber
                onChange={(value) => handleChange("length", parseNumber(value))}
              />

              <InputRow
                label={t("site.location.massKg")}
                value={vehicle?.mass}
                isNumber
                onChange={(value) => handleChange("mass", parseNumber(value))}
              />

              <InputRow
                label={t("site.location.cableLengthM")}
                value={vehicle?.cable_length}
                isNumber
                onChange={(value) =>
                  handleChange("cable_length", parseNumber(value))
                }
              />

              <InputRow
                label={t("site.location.turningRadiusM")}
                value={vehicle?.turning_radius}
                isNumber
                onChange={(value) =>
                  handleChange("turning_radius", parseNumber(value))
                }
              />
            </FormGrid>
          </FormSection>

          <FormSection title={t("site.location.capabilities")} className="col-span-1 md:col-span-2 mt-10">
            <FormGrid>
              <SwitchRow
                label={t("site.location.trailerParking")}
                checked={vehicle?.trailer_parking}
                onChange={(value) => handleChange("trailer_parking", value)}
              />

              <SwitchRow
                label={t("site.location.truckOnly")}
                checked={vehicle?.truck_only}
                onChange={(value) => handleChange("truck_only", value)}
              />

              <SwitchRow
                label={t("site.location.heightRestriction")}
                checked={vehicle?.height_restriction}
                onChange={(value) => handleChange("height_restriction", value)}
              />

              <SwitchRow
                label={t("site.location.driveThrough")}
                checked={vehicle?.drive_through}
                onChange={(value) => handleChange("drive_through", value)}
              />

              <SwitchRow
                label={t("site.location.dangerousGoods")}
                checked={vehicle?.dangerous_goods}
                onChange={(value) => handleChange("dangerous_goods", value)}
              />

              <SwitchRow
                label={t("site.location.rigidVehicles")}
                checked={vehicle?.rigid_vehicles}
                onChange={(value) => handleChange("rigid_vehicles", value)}
              />

              <SwitchRow
                label={t("site.location.overnightParking")}
                checked={vehicle?.overnight_parking}
                onChange={(value) => handleChange("overnight_parking", value)}
              />

              <SwitchRow
                label={t("site.location.barrier")}
                checked={vehicle?.barrier}
                onChange={(value) => handleChange("barrier", value)}
              />
            </FormGrid>
          </FormSection>

          <FormSection title={t("site.location.amenities")} className="col-span-1 mt-10">
            <SelectRow
              label={t("site.location.amenities")}
              value={vehicle?.amenities || []}
              options={amenityOptions}
              onChange={(value) => handleChange("amenities", value)}
              isMulti
            />
          </FormSection>
        </FormGrid>
      </FormCard>
    </div>
  );
}

export default HeavyVehicleEditor;
